import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Chip, Paper, Divider, TextField, Button,
    CircularProgress, Avatar, IconButton, Tooltip, useTheme,
    Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    AttachFile as AttachIcon,
    Send as SendIcon,
    Download as DownloadIcon,
    DeleteOutline as DeleteIcon,
    InsertDriveFile as FileIcon,
    AddCircleOutline as CreatedIcon,
    Sync as StatusChangedIcon,
    PersonAdd as AssignedIcon,
    PersonRemove as UnassignedIcon,
    CheckCircle as ResolvedIcon,
    ArrowUpward as EscalatedIcon,
    Flag as PriorityChangedIcon,
    Computer as HostIcon,
    Person as UserIcon,
    Wifi as IpIcon,
    Shield as ActionIcon,
    Hub as CorrelationIcon,
    TaskAlt as RecommendedIcon,
    Summarize as SummaryIcon,
} from '@mui/icons-material';
import { RootState, Ticket, TicketComment, TicketAttachment, TicketActivity, TicketActivityType, TicketPriority, TicketStatus } from '../../types';
import { IntegrationAlert } from '../../types/integration';
import {
    fetchTicket,
    fetchTicketComments,
    fetchTicketAttachments,
    fetchTicketActivity,
    addTicketComment,
    addTicketAttachment,
    deleteTicketAttachment,
    clientEscalateTicket,
    clearSelectedTicket,
} from '../../store/slices/ticketsSlice';
import { AppDispatch } from '../../store';
import { formatDate, getRelativeTimeString } from '../../utils/dateUtils';
import { getSeverityChipSx, getPriorityColor, getStatusChipSx, getStatusColor } from '../../theme/mappers/ticketMappers';
import { colorUtils } from '../../theme';
import api from '../../utils/axiosConfig';
import { markTicketViewed } from '../../utils/ticketViews';

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SLA_HOURS: Record<TicketPriority, number> = {
    [TicketPriority.CRITICAL]: 4,
    [TicketPriority.HIGH]: 24,
    [TicketPriority.MEDIUM]: 72,
    [TicketPriority.LOW]: 168,
};

// ── Section label ──────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const theme = useTheme();
    return (
        <Typography sx={{
            fontSize: '0.65rem', fontWeight: 700,
            color: theme.palette.text.tertiary,
            letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2,
        }}>
            {children}
        </Typography>
    );
};

// ── SLA progress bar ───────────────────────────────────────────────────────────

const SlaBar: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
    const theme = useTheme();
    const now = new Date();
    const start = new Date(ticket.created_at);
    const due = ticket.due_date
        ? new Date(ticket.due_date)
        : (() => { const d = new Date(start); d.setHours(d.getHours() + SLA_HOURS[ticket.priority]); return d; })();

    const isSettled = ticket.status === TicketStatus.RESOLVED || ticket.status === TicketStatus.CLOSED;
    const isBreached = !isSettled && now > due;

    const pct = isSettled ? 100
        : Math.min(100, Math.max(0, Math.round(
            (now.getTime() - start.getTime()) / (due.getTime() - start.getTime()) * 100
        )));

    const msRemaining = due.getTime() - now.getTime();
    const hRemaining = Math.floor(msRemaining / 3_600_000);
    const mRemaining = Math.floor((msRemaining % 3_600_000) / 60_000);
    const remainingLabel = hRemaining > 0 ? `${hRemaining}h ${mRemaining}m left` : `${mRemaining}m left`;

    const color = isSettled ? theme.palette.severity.low
        : isBreached ? theme.palette.severity.critical
        : pct >= 75 ? theme.palette.severity.high
        : pct >= 50 ? theme.palette.severity.medium
        : theme.palette.severity.low;

    const statusLabel = isSettled ? 'Resolved'
        : isBreached ? 'SLA breached'
        : remainingLabel;

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    SLA
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color }}>
                    {statusLabel}
                </Typography>
            </Box>
            <Box sx={{ height: 5, borderRadius: 3, backgroundColor: theme.palette.background.inset, overflow: 'hidden' }}>
                <Box sx={{
                    height: '100%', width: `${pct}%`, borderRadius: 3,
                    backgroundColor: color, transition: 'width 0.6s ease',
                }} />
            </Box>
            <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.tertiary, mt: 0.5 }}>
                Due {formatDate(due.toISOString())}
            </Typography>
        </Box>
    );
};

// ── Comment bubble ────────────────────────────────────────────────────────────

const CommentBubble: React.FC<{ comment: TicketComment; isOwnComment: boolean }> = ({
    comment, isOwnComment,
}) => {
    const theme = useTheme();
    const authorName = comment.author
        ? `${comment.author.first_name} ${comment.author.last_name}`.trim() || comment.author.username
        : 'Unknown';
    const isClient = comment.author?.role === 'client_user';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOwnComment ? 'flex-end' : 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: isOwnComment ? 'row-reverse' : 'row' }}>
                <Avatar sx={{
                    width: 24, height: 24, fontSize: '0.65rem', fontWeight: 700,
                    backgroundColor: isClient ? theme.palette.primary.main : theme.palette.secondary?.main || theme.palette.text.secondary,
                    color: '#fff',
                }}>
                    {authorName[0]?.toUpperCase()}
                </Avatar>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.text.primary }}>
                    {authorName}
                </Typography>
                <Chip
                    label={isClient ? 'You' : 'SOC Analyst'}
                    size="small"
                    sx={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        backgroundColor: isClient
                            ? colorUtils.withOpacity(theme.palette.primary.main, 0.12)
                            : colorUtils.withOpacity(theme.palette.text.secondary, 0.1),
                        color: isClient ? theme.palette.primary.main : theme.palette.text.secondary,
                        '& .MuiChip-label': { px: 0.75 },
                    }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>
                    {getRelativeTimeString(comment.created_at)}
                </Typography>
            </Box>
            <Box sx={{
                maxWidth: '75%', px: 2, py: 1.25,
                borderRadius: isOwnComment ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                backgroundColor: isOwnComment
                    ? colorUtils.withOpacity(theme.palette.primary.main, 0.1)
                    : theme.palette.background.elevated,
                border: `1px solid ${isOwnComment
                    ? colorUtils.withOpacity(theme.palette.primary.main, 0.2)
                    : theme.palette.border.light}`,
            }}>
                <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                    {comment.content}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Attachment bubble (inline in timeline) ────────────────────────────────────

const AttachmentBubble: React.FC<{ attachment: TicketAttachment; ticketId: string; onError: (msg: string) => void }> = ({
    attachment, ticketId, onError,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: RootState) => state.auth);
    const [downloading, setDownloading] = useState(false);
    const uploaderName = attachment.uploaded_by
        ? `${attachment.uploaded_by.first_name} ${attachment.uploaded_by.last_name}`.trim() || attachment.uploaded_by.username
        : 'Unknown';
    const isClient = attachment.uploaded_by?.role === 'client_user';
    const isOwn = attachment.uploaded_by?.id === user?.id;

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await api.get(`/tickets/${ticketId}/attachments/${attachment.id}`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url; a.download = attachment.filename; a.click();
            URL.revokeObjectURL(url);
        } catch (_) {
            onError(`Failed to download "${attachment.filename}". Please try again.`);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isClient ? 'flex-end' : 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexDirection: isClient ? 'row-reverse' : 'row' }}>
                <Avatar sx={{
                    width: 24, height: 24, fontSize: '0.65rem', fontWeight: 700,
                    backgroundColor: isClient ? theme.palette.primary.main : theme.palette.text.secondary,
                    color: '#fff',
                }}>
                    {uploaderName[0]?.toUpperCase()}
                </Avatar>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.palette.text.primary }}>{uploaderName}</Typography>
                <Chip
                    label={isClient ? 'You' : 'SOC Analyst'}
                    size="small"
                    sx={{
                        height: 18, fontSize: '0.6rem', fontWeight: 700,
                        backgroundColor: isClient
                            ? colorUtils.withOpacity(theme.palette.primary.main, 0.12)
                            : colorUtils.withOpacity(theme.palette.text.secondary, 0.1),
                        color: isClient ? theme.palette.primary.main : theme.palette.text.secondary,
                        '& .MuiChip-label': { px: 0.75 },
                    }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>
                    {getRelativeTimeString(attachment.created_at)}
                </Typography>
            </Box>
            <Box sx={{
                px: 2, py: 1.25,
                borderRadius: isClient ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                backgroundColor: theme.palette.background.elevated,
                border: `1px solid ${theme.palette.border.light}`,
                display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
                <FileIcon sx={{ fontSize: 18, color: theme.palette.text.tertiary, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500, color: theme.palette.text.primary }}>
                        {attachment.filename}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>
                        {formatFileSize(attachment.file_size)}
                    </Typography>
                </Box>
                <Tooltip title="Download">
                    <IconButton size="small" onClick={handleDownload} disabled={downloading}
                        sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.primary.main }, flexShrink: 0 }}>
                        {downloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                {isOwn && (
                    <Tooltip title="Remove">
                        <IconButton
                            size="small"
                            sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.error.main }, flexShrink: 0 }}
                            onClick={async () => {
                                try {
                                    await dispatch(deleteTicketAttachment({ ticketId, attachmentId: attachment.id })).unwrap();
                                } catch (_) {
                                    onError(`Failed to remove "${attachment.filename}". Please try again.`);
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

// ── Activity event row ────────────────────────────────────────────────────────

const ACTIVITY_CONFIG: Record<TicketActivityType, { Icon: React.ElementType; color: (theme: any) => string }> = {
    ticket_created:   { Icon: CreatedIcon,        color: t => t.palette.primary.main },
    status_changed:   { Icon: StatusChangedIcon,  color: t => t.palette.info?.main ?? t.palette.primary.main },
    assigned:         { Icon: AssignedIcon,       color: t => t.palette.primary.main },
    unassigned:       { Icon: UnassignedIcon,     color: t => t.palette.text.secondary },
    resolved:         { Icon: ResolvedIcon,       color: t => t.palette.severity.low },
    escalated:        { Icon: EscalatedIcon,      color: t => t.palette.severity.critical },
    priority_changed: { Icon: PriorityChangedIcon,color: t => t.palette.severity.medium },
};

const formatValue = (v: string | null): string => v ? v.replace(/_/g, ' ') : '';

const getActivityText = (event: TicketActivity): string => {
    const actor = event.actor
        ? `${event.actor.first_name} ${event.actor.last_name}`.trim() || event.actor.username
        : 'System';
    switch (event.type) {
        case 'ticket_created':   return `${actor} opened this incident`;
        case 'status_changed':   return `${actor} changed status from "${formatValue(event.from_value)}" to "${formatValue(event.to_value)}"`;
        case 'assigned':         return `${actor} assigned to ${formatValue(event.to_value)}`;
        case 'unassigned':       return `${actor} unassigned ${formatValue(event.from_value)}`;
        case 'resolved':         return `${actor} resolved this incident`;
        case 'escalated':        return `${actor} escalated this incident`;
        case 'priority_changed': return `${actor} changed priority from "${formatValue(event.from_value)}" to "${formatValue(event.to_value)}"`;
        default:                 return event.description ?? 'Activity recorded';
    }
};

const ActivityEventRow: React.FC<{ event: TicketActivity }> = ({ event }) => {
    const theme = useTheme();
    const cfg = ACTIVITY_CONFIG[event.type];
    const color = cfg.color(theme);
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box sx={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                backgroundColor: `${color}1a`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.1,
            }}>
                <cfg.Icon sx={{ fontSize: 14, color }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary, lineHeight: 1.5, textTransform: 'capitalize' }}>
                    {getActivityText(event)}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary, flexShrink: 0, mt: 0.2, whiteSpace: 'nowrap' }}>
                {getRelativeTimeString(event.created_at)}
            </Typography>
        </Box>
    );
};

// ── Asset row ─────────────────────────────────────────────────────────────────

const AssetRow: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({
    icon: Icon, label, value,
}) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
            <Icon sx={{ fontSize: 14, color: theme.palette.text.tertiary, mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.primary, fontFamily: value.match(/^\d{1,3}(\.\d{1,3}){3}$/) ? '"JetBrains Mono", monospace' : 'inherit', wordBreak: 'break-all' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ClientTicketDetailPage: React.FC = () => {
    const theme = useTheme();
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const { selectedTicket, comments, attachments, activity, isDetailsLoading } = useSelector((state: RootState) => state.tickets);

    const [commentText, setCommentText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [escalateOpen, setEscalateOpen] = useState(false);
    const [escalateReason, setEscalateReason] = useState('');
    const [escalateNote, setEscalateNote] = useState('');
    const [escalating, setEscalating] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const commentsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!id) return;
        dispatch(fetchTicket(id));
        dispatch(fetchTicketComments(id));
        dispatch(fetchTicketAttachments(id));
        dispatch(fetchTicketActivity(id));
        markTicketViewed(id);
        return () => { dispatch(clearSelectedTicket()); };
    }, [id, dispatch]);

    type TimelineItem =
        | { kind: 'comment';    id: string; ts: number; data: TicketComment    }
        | { kind: 'activity';   id: string; ts: number; data: TicketActivity   }
        | { kind: 'attachment'; id: string; ts: number; data: TicketAttachment };

    const timeline = useMemo((): TimelineItem[] => [
        ...comments.map(c    => ({ kind: 'comment'    as const, id: c.id, ts: new Date(c.created_at).getTime(),    data: c })),
        ...activity.map(a    => ({ kind: 'activity'   as const, id: a.id, ts: new Date(a.created_at).getTime(),   data: a })),
        ...attachments.map(a => ({ kind: 'attachment' as const, id: a.id, ts: new Date(a.created_at).getTime(), data: a })),
    ].sort((a, b) => a.ts - b.ts), [comments, activity, attachments]);

    useEffect(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [timeline.length]);

    // Aggregate affected assets and actions from linked integration alerts
    const incidentScope = useMemo(() => {
        const alerts: IntegrationAlert[] = selectedTicket?.integration_alerts ?? [];
        const uniq = <T,>(arr: (T | null | undefined)[]): T[] =>
            Array.from(new Set(arr.filter((v): v is T => v != null && (v as any) !== ''))) as T[];

        return {
            hostnames:  uniq(alerts.map(a => a.hostname)),
            ips:        uniq(alerts.map(a => a.ip_address)),
            usernames:  uniq(alerts.map(a => a.username)),
            fileNames:  uniq(alerts.map(a => a.file_name)),
            platforms:  uniq(alerts.map(a => a.integration_type)),
            actions:    uniq(alerts.map(a => a.action_taken)),
            alertCount: alerts.length,
            hasData:    alerts.length > 0,
            isMultiPlatform: uniq(alerts.map(a => a.integration_type)).length > 1,
        };
    }, [selectedTicket?.integration_alerts]);

    const handleSubmitComment = async () => {
        if (!commentText.trim() || !id) return;
        setSubmitting(true);
        await dispatch(addTicketComment({ ticketId: id, content: commentText.trim() }));
        setCommentText('');
        setSubmitting(false);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && id) dispatch(addTicketAttachment({ ticketId: id, file }));
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && id) dispatch(addTicketAttachment({ ticketId: id, file }));
    };

    const handleEscalate = async () => {
        if (!id || !escalateReason) return;
        setEscalating(true);
        const comment = [
            `🚩 Customer flag: ${escalateReason}`,
            escalateNote.trim() ? `\n${escalateNote.trim()}` : '',
        ].join('');
        await dispatch(clientEscalateTicket({ ticketId: id, comment }));
        setEscalating(false);
        setEscalateOpen(false);
        setEscalateReason('');
        setEscalateNote('');
        dispatch(fetchTicketActivity(id));
    };

    if (isDetailsLoading || !selectedTicket) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    const ticket = selectedTicket;
    const priorityColor = getPriorityColor(ticket.priority, theme);
    const statusColor = getStatusColor(ticket.status, theme);
    const isSettled = ticket.status === TicketStatus.RESOLVED || ticket.status === TicketStatus.CLOSED;
    const canEscalate = [
        TicketStatus.NEW, TicketStatus.ASSIGNED, TicketStatus.IN_PROGRESS, TicketStatus.PENDING,
    ].includes(ticket.status);

    const card = (children: React.ReactNode, extraSx?: object) => (
        <Paper elevation={0} sx={{
            p: 2.5, mb: 2, borderRadius: 2,
            border: `1px solid ${theme.palette.border.light}`,
            backgroundColor: theme.palette.background.paper,
            ...extraSx,
        }}>
            {children}
        </Paper>
    );

    return (
        <>
        <Box sx={{ minHeight: '100vh', px: { xs: 2, sm: 3, md: 4 }, py: 3, maxWidth: 1160 }}>
            {/* Back */}
            <Button startIcon={<BackIcon />} onClick={() => navigate('/client/tickets')}
                sx={{ mb: 2, color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}>
                Back to incidents
            </Button>

            {/* Correlation banner */}
            {(incidentScope.isMultiPlatform || incidentScope.alertCount > 1) && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2.5, py: 1.25, mb: 2.5, borderRadius: 1.5,
                    backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.08),
                    border: `1px solid ${colorUtils.withOpacity(theme.palette.warning.main, 0.25)}`,
                }}>
                    <CorrelationIcon sx={{ fontSize: 16, color: theme.palette.warning.main, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.8rem', color: theme.palette.warning.main }}>
                        <strong>Correlation:</strong>{' '}
                        {incidentScope.alertCount} alert{incidentScope.alertCount !== 1 ? 's' : ''} were grouped into this incident
                        {incidentScope.isMultiPlatform && ` across ${incidentScope.platforms.join(', ')}`}.
                    </Typography>
                </Box>
            )}

            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    <Chip label={ticket.priority} size="small" sx={{ ...getSeverityChipSx(priorityColor), backgroundColor: colorUtils.withOpacity(priorityColor, 0.1), '& .MuiChip-label': { px: 1 } }} />
                    <Chip label={ticket.status.replace(/_/g, ' ')} size="small" sx={{ ...getStatusChipSx(statusColor), '& .MuiChip-label': { px: 1 } }} />
                    <Chip label={ticket.id.slice(0, 8)} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', backgroundColor: 'transparent', color: theme.palette.text.tertiary, border: `1px solid ${theme.palette.border.main}`, '& .MuiChip-label': { px: 1 } }} />
                    {canEscalate && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EscalatedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setEscalateOpen(true)}
                            sx={{
                                ml: 'auto',
                                height: 26, px: 1.5,
                                fontSize: '0.72rem', fontWeight: 600,
                                borderColor: colorUtils.withOpacity(theme.palette.warning.main, 0.5),
                                color: theme.palette.warning.main,
                                '&:hover': {
                                    borderColor: theme.palette.warning.main,
                                    backgroundColor: colorUtils.withOpacity(theme.palette.warning.main, 0.06),
                                },
                            }}
                        >
                            Escalate
                        </Button>
                    )}
                </Box>
                <Typography variant="h1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {ticket.title}
                </Typography>
            </Box>

            {/* Two-column layout */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                {/* Left — description, resolution summary, activity */}
                <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
                    {/* Description */}
                    {ticket.description && card(
                        <>
                            <SectionLabel>Description</SectionLabel>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {ticket.description}
                            </Typography>
                        </>
                    )}

                    {/* Recommended client actions */}
                    {ticket.client_recommendations && card(
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <RecommendedIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
                                </Box>
                                <SectionLabel>Action required from you</SectionLabel>
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {ticket.client_recommendations}
                            </Typography>
                        </>,
                        { border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.3)}`, backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.04) }
                    )}

                    {/* Resolution summary */}
                    {isSettled && ticket.resolution_summary && card(
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: colorUtils.withOpacity(theme.palette.severity.low, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <SummaryIcon sx={{ fontSize: 15, color: theme.palette.severity.low }} />
                                </Box>
                                <SectionLabel>Resolution summary</SectionLabel>
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.primary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                                {ticket.resolution_summary}
                            </Typography>
                            {ticket.resolved_at && (
                                <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.tertiary, mt: 1.5 }}>
                                    Resolved {formatDate(ticket.resolved_at)}
                                </Typography>
                            )}
                        </>,
                        { border: `1px solid ${colorUtils.withOpacity(theme.palette.severity.low, 0.3)}` }
                    )}

                    {/* Activity timeline */}
                    <Paper
                        elevation={0}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        sx={{
                            p: 2.5, mb: 2, borderRadius: 2,
                            border: `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.border.light}`,
                            backgroundColor: isDragOver
                                ? colorUtils.withOpacity(theme.palette.primary.main, 0.04)
                                : theme.palette.background.paper,
                            transition: 'border-color 0.15s, background-color 0.15s',
                        }}
                    >
                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
                        <SectionLabel>Activity ({timeline.length})</SectionLabel>
                        {timeline.length === 0 ? (
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.tertiary, textAlign: 'center', py: 3 }}>
                                No activity recorded yet.
                            </Typography>
                        ) : (
                            <Box sx={{ mb: 2 }}>
                                {timeline.map(item =>
                                    item.kind === 'comment' ? (
                                        <CommentBubble key={item.id} comment={item.data} isOwnComment={item.data.author?.id === user?.id} />
                                    ) : item.kind === 'attachment' ? (
                                        <AttachmentBubble key={item.id} attachment={item.data} ticketId={ticket.id} onError={setDownloadError} />
                                    ) : (
                                        <ActivityEventRow key={item.id} event={item.data} />
                                    )
                                )}
                                <div ref={commentsEndRef} />
                            </Box>
                        )}
                        <Divider sx={{ borderColor: theme.palette.border.light, mb: 2 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                            <TextField fullWidth multiline minRows={2} maxRows={6}
                                placeholder="Write a comment or ask a question..."
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmitComment(); }}
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Tooltip title="Attach file">
                                <IconButton
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ height: 40, width: 40, borderRadius: 2, backgroundColor: theme.palette.background.tertiary, color: theme.palette.text.secondary, flexShrink: 0, '&:hover': { color: theme.palette.primary.main } }}
                                >
                                    <AttachIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Button variant="contained" onClick={handleSubmitComment}
                                disabled={!commentText.trim() || submitting}
                                sx={{ minWidth: 44, px: 1.5, height: 40, borderRadius: 2, flexShrink: 0 }}>
                                {submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
                            </Button>
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary, mt: 0.75 }}>
                            Cmd/Ctrl+Enter to send · drop files to attach
                        </Typography>
                    </Paper>
                </Box>

                {/* Right — metadata, scope, SOC actions, attachments */}
                <Box sx={{ width: 280, flexShrink: 0 }}>

                    {/* Details + SLA bar */}
                    {card(
                        <>
                            <SectionLabel>Details</SectionLabel>
                            <SlaBar ticket={ticket} />
                            <Divider sx={{ borderColor: theme.palette.border.light, mb: 2 }} />
                            {[
                                { label: 'Category',         value: ticket.category?.replace(/_/g, ' ') || '—' },
                                { label: 'Opened',           value: formatDate(ticket.created_at) },
                                { label: 'Last updated',     value: formatDate(ticket.updated_at) },
                                {
                                    label: 'Assigned analyst',
                                    value: ticket.assignee
                                        ? `${ticket.assignee.first_name} ${ticket.assignee.last_name[0]}.`.trim()
                                        : 'Unassigned',
                                },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ mb: 1.5 }}>
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: theme.palette.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        {label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: theme.palette.text.primary, mt: 0.25, textTransform: 'capitalize' }}>
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </>
                    )}

                    {/* Affected systems */}
                    {incidentScope.hasData && card(
                        <>
                            <SectionLabel>Affected systems</SectionLabel>
                            {incidentScope.hostnames.map(v  => <AssetRow key={v} icon={HostIcon}  label="Hostname"   value={v} />)}
                            {incidentScope.ips.map(v        => <AssetRow key={v} icon={IpIcon}    label="IP address" value={v} />)}
                            {incidentScope.usernames.map(v  => <AssetRow key={v} icon={UserIcon}  label="Account"    value={v} />)}
                            {incidentScope.fileNames.map(v  => <AssetRow key={v} icon={FileIcon}  label="File"       value={v} />)}
                            {incidentScope.hostnames.length === 0 && incidentScope.ips.length === 0 && incidentScope.usernames.length === 0 && (
                                <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.tertiary }}>
                                    No asset details available
                                </Typography>
                            )}
                        </>
                    )}

                    {/* SOC actions taken */}
                    {incidentScope.actions.length > 0 && card(
                        <>
                            <SectionLabel>SOC containment actions</SectionLabel>
                            {incidentScope.actions.map((action, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                    <ActionIcon sx={{ fontSize: 13, color: theme.palette.severity.low, mt: 0.35, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.primary, lineHeight: 1.5, textTransform: 'capitalize' }}>
                                        {action}
                                    </Typography>
                                </Box>
                            ))}
                        </>
                    )}

                </Box>
            </Box>
        </Box>

        <Snackbar
            open={!!downloadError}
            autoHideDuration={5000}
            onClose={() => setDownloadError(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ width: '100%' }}>
                {downloadError}
            </Alert>
        </Snackbar>

        <Dialog
            open={escalateOpen}
            onClose={() => !escalating && (setEscalateOpen(false), setEscalateReason(''), setEscalateNote(''))}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, backgroundColor: theme.palette.background.paper } }}
        >
            <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, pb: 1 }}>
                Flag for SOC attention
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary }}>
                    This will notify your SOC team. They will review and decide on next steps — the incident status will not change automatically.
                </Typography>
                <FormControl fullWidth size="small" required>
                    <InputLabel sx={{ fontSize: '0.85rem' }}>Reason for flagging</InputLabel>
                    <Select
                        value={escalateReason}
                        label="Reason for flagging"
                        onChange={e => setEscalateReason(e.target.value)}
                        disabled={escalating}
                        sx={{ fontSize: '0.85rem', borderRadius: 1.5 }}
                    >
                        <MenuItem value="Lack of updates" sx={{ fontSize: '0.85rem' }}>Lack of updates</MenuItem>
                        <MenuItem value="Situation has worsened" sx={{ fontSize: '0.85rem' }}>Situation has worsened</MenuItem>
                        <MenuItem value="New information discovered" sx={{ fontSize: '0.85rem' }}>New information discovered</MenuItem>
                        <MenuItem value="Executive visibility required" sx={{ fontSize: '0.85rem' }}>Executive visibility required</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    label="Additional context"
                    placeholder="Describe the change in impact..."
                    value={escalateNote}
                    onChange={e => setEscalateNote(e.target.value)}
                    disabled={escalating}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.85rem' } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={() => { setEscalateOpen(false); setEscalateReason(''); setEscalateNote(''); }}
                    disabled={escalating}
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleEscalate}
                    disabled={escalating || !escalateReason}
                    sx={{
                        fontSize: '0.82rem', fontWeight: 600, borderRadius: 1.5,
                        backgroundColor: theme.palette.warning.main,
                        '&:hover': { backgroundColor: theme.palette.warning.dark },
                    }}
                >
                    {escalating ? <CircularProgress size={16} color="inherit" /> : 'Send flag'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ClientTicketDetailPage;
